import { styled } from '@mui/material/styles';
import { ReactElement, useLayoutEffect, useRef, useState } from 'react';
import { IPortfolioChartProps } from '../../../../pages/PortfolioOverview/components/ChartsSection/ChartComponent';
import { allChartItems } from '../../../../pages/PortfolioOverview/components/ChartsSection/ChartsSection';
import { CHART_COMPONENTS, CoInvestorChartItem } from '../../../../types';
import { IconTypes } from '../../../Icon';
import { Chart, ChartSizes } from '../../chart-utils';
import { ChartTitleWrapper } from '../../ChartTitleWrapper';
import { ChartWrapper } from '../../ChartWrapper/ChartWrapper';
import { MostCommonCoInvestorsRow } from './MostCommonCoInvestorsRow';

interface Props extends Pick<IPortfolioChartProps<CoInvestorChartItem>, 'isFullScreen' | 'onOpenFullScreen'> {
  id: string;
  width: number;
  data: CoInvestorChartItem[];
}

const RowsWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 9px;
  text-align: center;
  height: 100%;
  overflow: auto;
`;

export function MostCommonCoInvestorsChart({
  id,
  width,
  data,
  isFullScreen,
  onOpenFullScreen,
}: Props): ReactElement {
  const [showMore, setShowMore] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [widthState, setWidthState] = useState<string | number>(width);
  const [size, setSize] = useState('1/2 Screen');
  const ref = useRef(null);

  useLayoutEffect(() => {
    const updateSize = () => {
      setWidthState(ChartSizes[size as keyof typeof ChartSizes]);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [size]);

  return (
    <ChartWrapper
      id={id}
      onMouseLeave={() => {
        setShowMore(false);
        setIsHovering(false);
      }}
      onMouseEnter={() => setIsHovering(true)}
      dataTestid={'mostCommonCoInvestorsChart'}
      width={widthState}
    >
      <ChartTitleWrapper
        isHovering={isHovering}
        title={
          allChartItems.get(CHART_COMPONENTS.MOST_COMMON_CO_INVESTORS)?.value ?? 'Most Common Co-Investors'
        }
        showMore={showMore}
        setShowMore={setShowMore}
        id={id}
        icon={IconTypes.STATISTICS_1}
        handleSizeChange={setSize}
        refProp={ref}
        size={size}
        isFullScreen={isFullScreen}
        handleFullScreen={onOpenFullScreen}
      />
      <Chart width={'100%'} height={isFullScreen ? '100%' : undefined} ref={ref}>
        <RowsWrapper>
          {data.map((i) => (
            <MostCommonCoInvestorsRow name={i.name} deals={i.deals} key={i.name} />
          ))}
        </RowsWrapper>
      </Chart>
    </ChartWrapper>
  );
}
